import {
    deriveBookingDetailsSections,
    formatDateWithTime,
    resolveAppointmentCancellation,
} from '@longy/common'
import { Button, ConfirmModal, CountDown, Skeleton, useToast } from '@longy/web-components'
import { createFileRoute, Link } from '@tanstack/react-router'
import { X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { BookingStep } from '../../components/booking/BookingStep'
import { api, getTRPCErrorMessage } from '../../core/trpc'
import { useLocale } from '../../hooks/useLocale'
import { BOOKING_BADGE_CLASSES, resolveBookingStatusBadge } from '../../utils/bookingList'

const PATH = '/bookings/$appointmentId'

const DetailRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-2 last:border-0">
        <span className="text-brand-gray">{label}</span>
        <span className="text-right font-semibold">{value}</span>
    </div>
)

const BookingDetailsPage = () => {
    const { t } = useTranslation()
    const { toast } = useToast()
    const { appointmentId } = Route.useParams()
    const { getLocalizedValue } = useLocale()
    const utils = api.useUtils()

    const [confirmOpen, setConfirmOpen] = useState(false)
    /**
     * Bumped to force a render, which re-reads the clock the cancellation state is derived from.
     *
     * The state is a function of `new Date()`, not of the payload, so when the server refuses a
     * cancellation the appointment comes back byte-identical and neither a refetch nor React Query's
     * structural sharing would re-render — leaving the live button that caused the refusal on screen.
     */
    const [, setClockVersion] = useState(0)
    const rereadClock = () => setClockVersion((version) => version + 1)

    const { data: appointment, isPending } = api.appointment.getByIdForUser.useQuery(
        { id: appointmentId },
        // The expert starts the consultation from admin; this poll is what turns the join button
        // on. Without it the page looks stuck at "not started yet".
        { refetchInterval: 15000 },
    )

    const { mutateAsync: cancelAppointment, isPending: isCancelling } =
        api.appointment.cancelVideoAppointment.useMutation()

    if (isPending) {
        return (
            <BookingStep
                title={t('frontend.bookings.details')}
                step={t('frontend.auth.myBookings')}
            >
                <Skeleton className="h-80 w-full" />
            </BookingStep>
        )
    }

    if (!appointment) {
        return (
            <BookingStep
                title={t('frontend.bookings.details')}
                step={t('frontend.auth.myBookings')}
            >
                <Link className="text-primary underline" to="/bookings">
                    {t('frontend.booking.backToBookings')}
                </Link>
            </BookingStep>
        )
    }

    // The same decision table the mobile details screen renders from, so the two clients cannot
    // disagree about whether this appointment offers a join button.
    const sections = deriveBookingDetailsSections({
        type: appointment.type,
        isCareOrExamination: appointment.isCareOrExamination,
        status: appointment.status,
        hasLocation: !!appointment.location,
    })

    const badge = resolveBookingStatusBadge(appointment.status)
    const canJoin = appointment.status === 'STARTED'
    // The same rule the app's details screen renders from and the server enforces, so the two
    // clients cannot disagree about whether this booking can still be cancelled.
    const cancellation = resolveAppointmentCancellation(appointment, new Date())

    const onCancel = async () => {
        try {
            await cancelAppointment({ appointmentId })
            toast({ description: t('frontend.bookings.cancelled'), variant: 'success' })
            await utils.appointment.invalidate()
        } catch (caught) {
            toast({ description: getTRPCErrorMessage(caught), variant: 'destructive' })
            // The server is the authority on the window, and the usual reason it refuses is that
            // this page was still rendering a clock from before the deadline. Re-read both, so the
            // button lands in the state the server just enforced.
            rereadClock()
            await utils.appointment.invalidate()
        } finally {
            setConfirmOpen(false)
        }
    }

    return (
        <BookingStep title={t('frontend.bookings.details')} step={t('frontend.auth.myBookings')}>
            <div className="flex flex-col gap-6">
                <span
                    className={`self-start rounded-full px-3 py-1 text-xs font-semibold ${BOOKING_BADGE_CLASSES[badge.variant]}`}
                >
                    {t(badge.labelKey)}
                </span>

                <section className="rounded-lg border border-slate-200 bg-white p-6">
                    <DetailRow
                        label={t(sections.serviceLabelKey)}
                        value={
                            appointment.serviceName
                                ? getLocalizedValue(appointment.serviceName)
                                : t(sections.serviceNameFallbackKey)
                        }
                    />
                    <DetailRow
                        label={t('frontend.booking.expert')}
                        value={appointment.expertName}
                    />
                    <DetailRow
                        label={t('frontend.booking.dateAndTime')}
                        value={formatDateWithTime(appointment.startDate)}
                    />
                    <DetailRow
                        label={t('frontend.booking.duration')}
                        value={`${appointment.durationInMinutes} ${t('frontend.booking.minutes')}`}
                    />
                    {sections.showLocationRow && appointment.location && (
                        <DetailRow
                            label={t('frontend.booking.location')}
                            value={appointment.location.name}
                        />
                    )}
                </section>

                {sections.showCountDown && (
                    <div className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 bg-white p-6">
                        <span className="text-sm text-brand-gray">
                            {t('frontend.bookings.startsIn')}
                        </span>
                        <CountDown endTime={appointment.startDate} className="text-lg font-bold" />
                    </div>
                )}

                {sections.additionalInfoBodyKey && (
                    <p className="rounded-lg bg-slate-50 p-4 text-sm text-brand-gray">
                        {t(sections.additionalInfoBodyKey)}
                    </p>
                )}

                {sections.showVideoJoinButton && (
                    <div className="flex flex-col gap-2">
                        <Button asChild={canJoin} disabled={!canJoin}>
                            {canJoin ? (
                                <Link to="/bookings/meet/$appointmentId" params={{ appointmentId }}>
                                    {t('frontend.bookings.join')}
                                </Link>
                            ) : (
                                <span>{t('frontend.bookings.join')}</span>
                            )}
                        </Button>
                        {!canJoin && (
                            <p className="text-center text-sm text-brand-gray">
                                {t('frontend.bookings.joinNotStartedYet')}
                            </p>
                        )}
                    </div>
                )}

                {cancellation.showSection && (
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <X className="h-5 w-5 text-primary" />
                            <h2 className="text-lg font-bold">
                                {t('app.booking.cancelAppointmentTitle')}
                            </h2>
                        </div>
                        {/* The same keys the app's details screen renders, so the two clients
                            cannot state different terms. */}
                        <div className="flex flex-col gap-1 text-sm">
                            <p className="font-semibold">
                                {t('app.booking.cancellationPolicyTitle')}
                            </p>
                            <p className="text-brand-gray">{t(cancellation.policyHintKey)}</p>
                        </div>
                        {/* The section stays on screen once the window closes: the point is to say
                            why the button is dead, not to make it vanish. */}
                        {!!cancellation.blockedReasonKey && (
                            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700">
                                {t(cancellation.blockedReasonKey)}
                            </p>
                        )}
                        <Button
                            variant="destructive"
                            disabled={!cancellation.canCancel || isCancelling}
                            onClick={() => setConfirmOpen(true)}
                        >
                            {t('frontend.bookings.cancelBooking')}
                        </Button>
                    </div>
                )}
            </div>

            <ConfirmModal
                open={confirmOpen}
                setOpen={setConfirmOpen}
                title={t('frontend.bookings.cancelConfirmTitle')}
                description={t('frontend.bookings.cancelConfirmBody')}
                onConfirm={onCancel}
            />
        </BookingStep>
    )
}

export const Route = createFileRoute(PATH)({
    head: () => ({ meta: [{ title: 'Long-Y - Foglalás részletei' }] }),
    component: BookingDetailsPage,
})
